@if (isInline() || (isSub() && openSubmenusInline())) {
  <ul
    role="menu"
    [attr.aria-label]="ariaLabel() ?? ((isSub() ? 'header.aria.submenu' : 'header.aria.navigationMenu') | translate)"
    [class]="`z-20 flex flex-col ${menuClasses()}`"
    [ngClass]="{
      hidden: !opened(),
      'ms-4': isSub() && opened()
    }"
    (keydown)="handleKeydown($event)"
    (focusout)="handleFocusout($event)"
  >
    <ng-container [ngTemplateOutlet]="innerDropdown"></ng-container>
  </ul>
} @else {
  <ul
    role="menu"
    [attr.aria-label]="ariaLabel() ?? ((isSub() ? 'header.aria.submenu' : 'header.aria.navigationMenu') | translate)"
    [attr.aria-hidden]="!opened()"
    class="absolute z-20 flex flex-col duration-200 ease-in-out transition-[opacity,translate] animate-transition"
    [ngClass]="menuClasses()"
    [ngStyle]="{
      top: topPositionStyle(),
      left: leftPositionStyle(),
    }"
    [class.-translate-y-2]="!opened()"
    [class.translate-y-0]="opened()"
    [class.opacity-0]="!opened()"
    [class.invisible]="!opened()"
    [class.max-h-0]="!opened()"
    [class.overflow-hidden]="!opened()"
    [class.pointer-events-none]="!opened()"
    [class.opacity-100]="opened()"
    [class.visible]="opened()"
    [class.pointer-events-auto]="opened()"
    (keydown)="handleKeydown($event)"
    (focusout)="handleFocusout($event)"
  >
    @if (hoverBridgeHeight() > 0) {
      <!-- Invisible strip over the gap up to the trigger: hovering it keeps the trigger hovered, so the menu stays open -->
      <li
        role="none"
        aria-hidden="true"
        class="absolute bottom-full left-0"
        [style.width.px]="hoverBridgeWidth()"
        [style.height.px]="hoverBridgeHeight()"
      ></li>
    }
    <ng-container [ngTemplateOutlet]="innerDropdown"></ng-container>
  </ul>
}

<ng-template #innerDropdown>
  @for (item of items(); track $index) {
    @let active = isItemActive(item);
    @if (item.items.length > 0) {
      <li
        role="none"
        class="relative"
        (mouseenter)="!openSubmenusInline() && subDropdown.toggleDropdown(true, $event, item)"
        (mouseleave)="!openSubmenusInline() && subDropdown.toggleDropdown(false, $event, item)"
      >
        @if (item.linkUrl) {
          <div
            role="menuitem"
            [attr.tabindex]="opened() ? 0 : -1"
            [attr.aria-expanded]="item.opened ?? false"
            [attr.aria-current]="active ? 'page' : null"
            aria-haspopup="menu"
            [class]="`focus-visible:ring-primary-main flex w-full items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${menuItemClasses()}`"
            [ngClass]="[hoverMenuItemClasses(), itemColorClasses(active)]"
            (keydown.enter)="activateItem(item, $event)"
            (keydown.space)="activateItem(item, $event)"
          >
            @let isExternalLink = item.linkUrl?.startsWith('http');
            <a
              [href]="item.linkUrl"
              [target]="isExternalLink ? '_blank' : (item.linkTarget ?? '_self')"
              [attr.rel]="isExternalLink ? 'noopener noreferrer' : null"
              tabindex="-1"
              class="flex flex-1 items-center"
              [ngClass]="{ underline: active && activeUnderlined() }"
            >
              @if (isSub() && showItemPhotos() && item.photo) {
                <gs-optimized-image
                  [photo]="item.photo"
                  size="small"
                  mode="fill"
                  sizes="48px"
                  [alt]="item.photo.alt ?? item.photo.title ?? item.label"
                  [containerClass]="'size-12 shrink-0 rounded-lg ' + photoMarginClass()"
                  objectFit="object-cover"
                />
              } @else if (!isSub() && showItemGlyphs() && item.glyph) {
                <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                  <gs-icon [iconName]="item.glyph" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
                </span>
              }
              {{ item.label }}
            </a>
            <button
              type="button"
              tabindex="-1"
              [attr.aria-label]="(item.opened ? 'header.aria.closeSubmenu' : 'header.aria.openSubmenu') | translate"
              (click)="toggleSubmenu(item, $event)"
              (keydown.enter)="toggleSubmenu(item, $event)"
              (keydown.space)="toggleSubmenu(item, $event)"
              class="ml-2 p-1"
            >
              <gs-icon [iconName]="menuChevronIcon(item)" [iconWeight]="openSubmenusInline() ? 'regular' : 'light'" [attr.aria-hidden]="true"></gs-icon>
            </button>
          </div>
        } @else {
          <!-- Sub-items only (no link) -->
          <button
            type="button"
            role="menuitem"
            [attr.aria-expanded]="item.opened ?? false"
            aria-haspopup="menu"
            [attr.tabindex]="opened() ? 0 : -1"
            (click)="activateItem(item, $event)"
            (keydown.enter)="activateItem(item, $event)"
            (keydown.space)="activateItem(item, $event)"
            [class]="`focus-visible:ring-primary-main flex w-full items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${menuItemClasses()}`"
            [ngClass]="[hoverMenuItemClasses(), menuItemColorClasses()]"
          >
            <span class="flex flex-1 items-center">
              @if (isSub() && showItemPhotos() && item.photo) {
                <gs-optimized-image
                  [photo]="item.photo"
                  size="small"
                  mode="fill"
                  sizes="48px"
                  [alt]="item.photo.alt ?? item.photo.title ?? item.label"
                  [containerClass]="'size-12 shrink-0 rounded-lg ' + photoMarginClass()"
                  objectFit="object-cover"
                />
              } @else if (!isSub() && showItemGlyphs() && item.glyph) {
                <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                  <gs-icon [iconName]="item.glyph" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
                </span>
              }
              {{ item.label }}
            </span>
            <gs-icon [iconName]="menuChevronIcon(item)" [iconWeight]="openSubmenusInline() ? 'regular' : 'light'" [attr.aria-hidden]="true"></gs-icon>
          </button>
        }
        <dropdown
          #subDropdown
          [items]="item.items"
          [opened]="item.opened ?? false"
          [isSub]="true"
          [parentItem]="item"
          [openSubmenusInline]="openSubmenusInline()"
          [showItemPhotos]="showItemPhotos()"
          [showItemGlyphs]="showItemGlyphs()"
          [glyphMarginClass]="glyphMarginClass()"
          [photoMarginClass]="photoMarginClass()"
          [menuClasses]="menuClasses()"
          [menuItemClasses]="menuItemClasses()"
          [menuItemColorClasses]="menuItemColorClasses()"
          [activeMenuItemClasses]="activeMenuItemClasses()"
          [hoverMenuItemClasses]="hoverMenuItemClasses()"
          [activeUnderlined]="activeUnderlined()"
          [ariaLabel]="'header.aria.namedSubmenu' | translate: { label: item.label }"
        ></dropdown>
      </li>
    } @else {
      <li role="none">
        @if (item.linkUrl) {
          <!-- Link only (no sub-items) -->
          @let isExternalUrl = item.linkUrl?.startsWith('http');
          <a
            role="menuitem"
            [href]="item.linkUrl"
            [target]="isExternalUrl ? '_blank' : (item.linkTarget ?? '_self')"
            [attr.rel]="isExternalUrl ? 'noopener noreferrer' : null"
            [attr.tabindex]="opened() ? 0 : -1"
            [attr.aria-current]="active ? 'page' : null"
            [class]="`focus-visible:ring-primary-main flex w-full items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${menuItemClasses()}`"
            [ngClass]="[hoverMenuItemClasses(), itemColorClasses(active), active && activeUnderlined() ? 'underline' : '']"
          >
            @if (isSub() && showItemPhotos() && item.photo) {
              <gs-optimized-image
                [class]="'block size-12 shrink-0 ' + photoMarginClass()"
                [photo]="item.photo"
                size="small"
                mode="square"
                sizes="48px"
                [alt]="item.photo.alt ?? item.photo.title ?? item.label"
                [seoDescription]="item.photo.title ?? item.label ?? ''"
                containerClass="rounded-lg"
                objectFit="object-cover"
              />
            } @else if (!isSub() && showItemGlyphs() && item.glyph) {
              <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                <gs-icon [iconName]="item.glyph" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
              </span>
            }
            {{ item.label }}
          </a>
        } @else {
          <span
            role="menuitem"
            [attr.aria-disabled]="true"
            [attr.tabindex]="-1"
            [class]="`flex w-full cursor-not-allowed items-center opacity-60 ${menuItemClasses()}`"
            [ngClass]="[menuItemColorClasses()]"
          >
            @if (isSub() && showItemPhotos() && item.photo) {
              <gs-optimized-image
                [class]="'block size-12 shrink-0 ' + photoMarginClass()"
                [photo]="item.photo"
                size="small"
                mode="square"
                sizes="48px"
                [alt]="item.photo.alt ?? item.photo.title ?? item.label"
                [seoDescription]="item.photo.title ?? item.label ?? ''"
                containerClass="rounded-lg"
                objectFit="object-cover"
              />
            } @else if (!isSub() && showItemGlyphs() && item.glyph) {
              <span class="inline-flex size-6 shrink-0 items-center justify-center" [class]="glyphMarginClass()">
                <gs-icon [iconName]="item.glyph" iconWeight="light" [attr.aria-hidden]="true"></gs-icon>
              </span>
            }
            {{ item.label }}
          </span>
        }
      </li>
    }
  }
</ng-template>
